import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './lib/auth.jsx'
import AppLayout from './components/shared/AppLayout'
import LoadingScreen from './components/shared/LoadingScreen'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import PatientsPage from './pages/PatientsPage'
const PatientDetailPage = lazy(() => import('./pages/PatientDetailPage'))
import SafetyWorklistPage from './pages/SafetyWorklistPage'
import ConsultationsPage from './pages/ConsultationsPage'
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
import AssessmentsPage from './pages/AssessmentsPage'
const TelemedPage = lazy(() => import('./pages/TelemedPage'))
const SchedulingPage = lazy(() => import('./pages/SchedulingPage'))
import BridgeEntryPage from './pages/BridgeEntryPage'
const NeurologyDocumentationPage = lazy(() => import('./pages/NeurologyDocumentationPage'))
const PsychiatryWorkspacePage = lazy(() => import('./pages/PsychiatryWorkspacePage'))
import NotFoundPage from './pages/NotFoundPage'
const InitialPsychiatricEvaluationRoute = lazy(() => import('./routes/InitialPsychiatricEvaluationRoute'))
const BehavioralHealthEvaluationRoute = lazy(() => import('./routes/BehavioralHealthEvaluationRoute'))
const PsychotherapyProgressNoteRoute = lazy(() => import('./routes/PsychotherapyProgressNoteRoute'))
const TestingEpisodesPage = lazy(() => import('./pages/TestingEpisodesPage'))
const FollowUpVisitPage = lazy(() => import('./pages/FollowUpVisitPage').then(m => ({ default: m.FollowUpVisitPage })))
const MedicationManagementVisitPage = lazy(() => import('./pages/MedicationManagementVisitPage').then(m => ({ default: m.MedicationManagementVisitPage })))
const InpatientInitialExaminationPage = lazy(() => import('./pages/InpatientInitialExaminationPage').then(m => ({ default: m.InpatientInitialExaminationPage })))
const SubsequentInpatientVisitPage = lazy(() => import('./pages/SubsequentInpatientVisitPage').then(m => ({ default: m.SubsequentInpatientVisitPage })))
const OfficeVisitPage = lazy(() => import('./pages/OfficeVisitPage').then(m => ({ default: m.OfficeVisitPage })))
const DischargeSummaryPage = lazy(() => import('./pages/DischargeSummaryPage').then(m => ({ default: m.DischargeSummaryPage })))
const AppPage = lazy(() => import('./pages/App'))
import { useEffect, useState, lazy, Suspense } from 'react'
import { bridgeApi } from './lib/supabase'

// ── Auth guard ────────────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { isAuthenticated, mfaSatisfied, loading } = useAuth()
  if (loading)          return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  // Enforce MFA at the route layer: a password-only session (mfa enabled but
  // not verified this session) cannot reach /app by direct navigation.
  if (!mfaSatisfied)    return <Navigate to="/login" replace />
  return children
}

// ── Cross-app bridge entry ─────────────────────────────────────────────────────
function BridgeRoute() {
  const [status, setStatus] = useState('validating')
  const [error,  setError]  = useState(null)

  useEffect(() => {
    const session = bridgeApi.parseFromUrl()
    if (!session) { setStatus('error'); setError('Missing session parameters.'); return }

    bridgeApi.validate(
      session.token, session.encounter_id,
      session.patient_id, session.provider_id, session.org_id
    )
    .then(() => setStatus('valid'))
    .catch(err  => { setStatus('error'); setError(err.message) })
  }, [])

  if (status === 'validating') return <LoadingScreen message="Validating session from PatientTracForge…" />
  if (status === 'error')      return <BridgeEntryPage error={error} />
  return <AppLayout><AppPage /></AppLayout>
}

// ── Auth callback (magic link / password reset) ───────────────────────────────
function CallbackPage() {
  const { isAuthenticated } = useAuth()
  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />
  return <LoadingScreen message="Completing sign in…" />
}

export default function App({ isBridgeMode }) {
  const { loading } = useAuth()
  if (loading && !isBridgeMode) return <LoadingScreen />

  return (
    <Router>
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public */}
        <Route path="/"        element={<LandingPage />} />
        <Route path="/login"   element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/callback"element={<CallbackPage />} />

        {/* Cross-app bridge — launched from PatientTracForge */}
        {isBridgeMode && <Route path="*" element={<BridgeRoute />} />}

        {/* Protected app routes */}
        {!isBridgeMode && (
          <Route path="/app" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index                  element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard"       element={<DashboardPage />} />
            <Route path="provider"        element={<Navigate to="/app/dashboard" replace />} />
            <Route path="patients"        element={<PatientsPage />} />
            <Route path="patients/:id"    element={<PatientDetailPage />} />
            <Route path="assessments"     element={<AssessmentsPage />} />
            <Route path="safety"          element={<SafetyWorklistPage />} />
            <Route path="consultations"   element={<ConsultationsPage />} />
            <Route path="reports"         element={<ReportsPage />} />
            <Route path="telemed"         element={<TelemedPage />} />
            <Route path="scheduling"      element={<SchedulingPage />} />
            <Route path="neurology"       element={<NeurologyDocumentationPage />} />

            {/* Psychiatry exam routes */}
            <Route path="clinical"                      element={<PsychiatryWorkspacePage />} />
            <Route path="encounter"                     element={<AppPage />} />
            <Route path="exams/initial/:encounterId"    element={<InitialPsychiatricEvaluationRoute />} />
            {/* Shared Initial Behavioral Health Evaluation — three locked variants */}
            <Route path="evaluations/:type"                element={<BehavioralHealthEvaluationRoute />} />
            <Route path="notes/psychotherapy"              element={<PsychotherapyProgressNoteRoute />} />
            <Route path="testing"                          element={<TestingEpisodesPage />} />
            <Route path="exams/followup"                element={<FollowUpVisitPage />} />
            <Route path="exams/medication-management"   element={<MedicationManagementVisitPage />} />
            <Route path="exams/inpatient-initial"       element={<InpatientInitialExaminationPage />} />
            <Route path="exams/subsequent-inpatient"    element={<SubsequentInpatientVisitPage />} />
            <Route path="exams/office"                  element={<OfficeVisitPage />} />
            <Route path="exams/discharge"               element={<DischargeSummaryPage />} />
          </Route>
        )}

        {!isBridgeMode && <Route path="*" element={<NotFoundPage />} />}
      </Routes>
      </Suspense>
    </Router>
  )
}
